const DEFAULT_TIME_ZONE = "Europe/Berlin";
const dateTimeFormatterCache = new Map();
function getDateTimeFormatter(timeZone) {
    const resolvedTimeZone = getTenantTimeZone(timeZone);
    if (!dateTimeFormatterCache.has(resolvedTimeZone)) {
        dateTimeFormatterCache.set(resolvedTimeZone, new Intl.DateTimeFormat("en-CA", {
            timeZone: resolvedTimeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hourCycle: "h23",
        }));
    }
    return dateTimeFormatterCache.get(resolvedTimeZone);
}
function normalizeDateInput(value) {
    const date = value instanceof Date ? new Date(value) : new Date(value);
    if (Number.isNaN(date.getTime())) {
        throw new Error(`Invalid date input: ${String(value)}`);
    }
    return date;
}
function padNumber(value) {
    return value.toString().padStart(2, "0");
}
function parseDateKey(dateKey) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
    if (!match) {
        throw new Error(`Invalid date key: ${dateKey}`);
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return { year, month, day };
}
function parseTimeKey(timeKey) {
    const match = /^(\d{2}):(\d{2})$/.exec(timeKey);
    if (!match) {
        throw new Error(`Invalid time key: ${timeKey}`);
    }
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    return { hour, minute };
}
function formatDateKeyFromUtcDate(date) {
    return `${date.getUTCFullYear()}-${padNumber(date.getUTCMonth() + 1)}-${padNumber(date.getUTCDate())}`;
}
function formatTimeKeyFromUtcDate(date) {
    return `${padNumber(date.getUTCHours())}:${padNumber(date.getUTCMinutes())}`;
}
function getTimeZoneOffsetMilliseconds(date, timeZone) {
    const parts = getTimeZoneDateTimeParts(date, timeZone);
    const zonedAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return zonedAsUtc - date.getTime();
}
export function getTenantTimeZone(timeZone) {
    const resolved = timeZone?.trim();
    return resolved || DEFAULT_TIME_ZONE;
}
export function getPlainDateKey(date) {
    return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;
}
export function dateKeyToLocalDate(dateKey, hour = 12) {
    const { year, month, day } = parseDateKey(dateKey);
    return new Date(year, month - 1, day, hour, 0, 0, 0);
}
export function shiftDateKey(dateKey, days) {
    const { year, month, day } = parseDateKey(dateKey);
    const date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    date.setUTCDate(date.getUTCDate() + days);
    return formatDateKeyFromUtcDate(date);
}
export function getTimeZoneDateTimeParts(value, timeZone = DEFAULT_TIME_ZONE) {
    const formatter = getDateTimeFormatter(timeZone);
    const date = normalizeDateInput(value);
    const result = formatter.formatToParts(date).reduce((parts, part) => {
        if (part.type !== "literal") {
            parts[part.type] = Number(part.value);
        }
        return parts;
    }, {});
    return {
        year: result.year,
        month: result.month,
        day: result.day,
        hour: result.hour,
        minute: result.minute,
        second: result.second,
    };
}
export function getTimeZoneDateKey(value, timeZone = DEFAULT_TIME_ZONE) {
    const parts = getTimeZoneDateTimeParts(value, timeZone);
    return `${parts.year}-${padNumber(parts.month)}-${padNumber(parts.day)}`;
}
export function getTimeZoneTimeKey(value, timeZone = DEFAULT_TIME_ZONE) {
    const parts = getTimeZoneDateTimeParts(value, timeZone);
    return `${padNumber(parts.hour)}:${padNumber(parts.minute)}`;
}
export function getMinutesFromMidnightInTimeZone(value, timeZone = DEFAULT_TIME_ZONE) {
    const parts = getTimeZoneDateTimeParts(value, timeZone);
    return parts.hour * 60 + parts.minute;
}
export function getCurrentDateInTimeZone(timeZone = DEFAULT_TIME_ZONE) {
    return dateKeyToLocalDate(getTimeZoneDateKey(new Date(), timeZone));
}
export function isSameDateInTimeZone(value, dateKey, timeZone = DEFAULT_TIME_ZONE) {
    return getTimeZoneDateKey(value, timeZone) === dateKey;
}
export function toTimeZoneWallClockDate(value, timeZone = DEFAULT_TIME_ZONE) {
    const parts = getTimeZoneDateTimeParts(value, timeZone);
    return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, 0);
}
export function convertLocalDateTimeToUtc(dateKey, timeKey, timeZone = DEFAULT_TIME_ZONE) {
    const { year, month, day } = parseDateKey(dateKey);
    const { hour, minute } = parseTimeKey(timeKey);
    const naiveUtc = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    let resolvedUtc = naiveUtc - getTimeZoneOffsetMilliseconds(new Date(naiveUtc), timeZone);
    const adjustedUtc = naiveUtc - getTimeZoneOffsetMilliseconds(new Date(resolvedUtc), timeZone);
    if (adjustedUtc !== resolvedUtc) {
        resolvedUtc = adjustedUtc;
    }
    return new Date(resolvedUtc);
}
export function getUtcRangeForLocalDate(dateKey, timeZone = DEFAULT_TIME_ZONE) {
    return getUtcRangeForLocalDateRange(dateKey, dateKey, timeZone);
}
export function getUtcRangeForLocalDateRange(startDateKey, endDateKey, timeZone = DEFAULT_TIME_ZONE) {
    return {
        start: convertLocalDateTimeToUtc(startDateKey, "00:00", timeZone),
        endExclusive: convertLocalDateTimeToUtc(shiftDateKey(endDateKey, 1), "00:00", timeZone),
    };
}
export function addMinutesToLocalDateTime(dateKey, timeKey, minutesToAdd) {
    const { year, month, day } = parseDateKey(dateKey);
    const { hour, minute } = parseTimeKey(timeKey);
    const date = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
    date.setUTCMinutes(date.getUTCMinutes() + minutesToAdd);
    return {
        dateKey: formatDateKeyFromUtcDate(date),
        timeKey: formatTimeKeyFromUtcDate(date),
    };
}
